import { useState, useEffect, useRef } from 'react';

import { FormikProps } from 'formik';

interface UseAddressVerificationProps {
  googleAPILoaded: boolean;
  formik: FormikProps<any>;
  setActiveErrorModal: (active: boolean) => void;
  setErrorInformation: (error: { errorMessage: string }) => void;
}

export const useAddressVerification = ({
  googleAPILoaded,
  formik,
  setActiveErrorModal,
  setErrorInformation
}: UseAddressVerificationProps) => {
  const [addressVerificationFail, setAddressVerificationFail] = useState<boolean>(false);
  const [withoutAddressVerification, setWithoutAddressVerification] = useState<boolean>(false);
  const [isAutocompleteInProgress, setIsAutocompleteInProgress] = useState<boolean>(false);
  const addressInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (googleAPILoaded && addressInputRef.current) {
      initializeAutocomplete();
    }
  }, [googleAPILoaded]);

  const initializeAutocomplete = () => {
    if (!addressInputRef.current || !window.google?.maps?.places) return;

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          componentRestrictions: { country: ['us', 'ca', 'pr'] },
          fields: ['address_components', 'geometry'],
          types: ['address'],
        },
      );

      autocomplete.addListener('place_changed', () => {
        setIsAutocompleteInProgress(true);
        
        const place = autocomplete.getPlace();

        if (!place.geometry) {
          console.log('No details available for this place');
          setIsAutocompleteInProgress(false);
          return;
        }

        const addressComponents = place.address_components || [];

        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zipCode = '';

        addressComponents.forEach((component) => {
          const types = component.types;

          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            route = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          } else if (types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        });

        // Construct address string
        const address = streetNumber ? `${streetNumber} ${route}` : route;

        // Update form fields
        formik.setFieldValue('address1', address);
        formik.setFieldValue('city', city);
        formik.setFieldValue('state', state);
        formik.setFieldValue('zipCode', zipCode);

        // Mark fields as touched
        formik.setFieldTouched('address1', true);
        formik.setFieldTouched('city', true);
        formik.setFieldTouched('state', true);
        formik.setFieldTouched('zipCode', true);

        // Validate form after updating fields and reset autocomplete flag
        setTimeout(() => {
          formik.validateForm();
          setTimeout(() => {
            setIsAutocompleteInProgress(false);
          }, 300);
        }, 0);
      });
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      setActiveErrorModal(true);
      setErrorInformation({ errorMessage: 'error.google.api.autocomplete' });
    }
  };

  return {
    addressInputRef,
    addressVerificationFail,
    setAddressVerificationFail,
    withoutAddressVerification,
    setWithoutAddressVerification,
    isAutocompleteInProgress
  };
};